import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { environment } from '@core/environments/environment';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ECampaignStatus } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toCampaignApiPayload } from '@pages/campaigns/brief/brief-mapping.util';
import { IBriefAnalysis, IBriefValidationMessage } from '@pages/campaigns/brief/brief.interface';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { BaseComponent } from '@shared/components/base.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { fileExtensionValidator } from '@shared/validators/file-upload.validator';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { merge, Observable, of } from 'rxjs';
import { BriefWarningsDialogComponent } from './brief-wizard-dialog/brief-warnings-dialog/brief-warnings-dialog.component';
import { BriefWizardDialogComponent } from './brief-wizard-dialog/brief-wizard-dialog.component';
import { TBriefWizardDialogResult } from './brief-wizard-dialog/brief-wizard-dialog.interface';
import {
  ICreateCampaignDialogData,
  ICreateCampaignDialogForm,
  TCreateCampaignDialogMode,
} from './create-campaign-dialog.interface';
import { CreateCampaignDialogStore } from './create-campaign-dialog.store';

const BRIEF_FILE_EXTENSIONS = ['pdf', 'docx', 'txt'];

@Component({
  imports: [
    A11yModule,
    SideDialogComponent,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    DotsSpinnerComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatErrorComponent,
    FileUploaderComponent,
    IconComponent,
  ],
  providers: [CreateCampaignDialogStore],
  selector: 'app-create-campaign-dialog',
  templateUrl: './create-campaign-dialog.component.html',
})
export class CreateCampaignDialogComponent extends BaseComponent implements OnDestroy {
  public readonly form: FormGroup<ICreateCampaignDialogForm> = new FormGroup({
    briefFile: new FormControl<ICustomFile | null>(null, fileExtensionValidator(BRIEF_FILE_EXTENSIONS)),
    iconId: new FormControl<string | null>(null),
    imageFile: new FormControl<ICustomFile | null>(null, imageExtensionValidator()),
    name: new FormControl<string | null>(null, Validators.required),
  });

  // Never triggers a real upload - the raw File is all the brief flow needs (a multipart POST to
  // /brief/analyze), not a stored-file id. Passed to the second app-file-uploader's
  // [uploadRequest] to bypass its default ImageUtilitiesService.uploadFile() call, which would
  // otherwise push the brief document to the generic file-storage endpoint for no reason.
  public readonly briefFileUploadStub = (): Observable<string> => of('');

  public readonly data = inject<ICreateCampaignDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(CreateCampaignDialogStore);
  private readonly dialogRef = inject(MatDialogRef<CreateCampaignDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly copySubmitRequested = signal<boolean>(false);
  private briefModeSubmitted = false;

  private readonly briefAnalyzingOverlayTemplate = viewChild<TemplateRef<void>>('briefAnalyzingOverlay');
  private briefAnalyzingOverlayRef?: OverlayRef;

  public readonly mode = computed<TCreateCampaignDialogMode>(() => {
    if (this.data.mode) {
      return this.data.mode;
    }

    if (this.data.campaign && this.data.campaignUpdatePayload) {
      return 'edit';
    }

    return 'create';
  });
  public readonly isCopyMode = computed<boolean>(() => this.mode() === 'copy');
  public readonly isEditMode = computed<boolean>(() => this.mode() === 'edit');
  public readonly showBriefUploader = computed<boolean>(
    () => environment.campaignCreationByPromptEnabled && !this.isEditMode() && !this.isCopyMode(),
  );
  public readonly submitButtonLoading = computed<boolean>(() => {
    return (
      this.store.campaignSubmitLoading$() ||
      this.store.briefAnalyzeLoading$() ||
      (this.isCopyMode() && this.copySubmitRequested())
    );
  });

  public readonly title = computed(() => {
    if (this.isCopyMode()) {
      return this.translate.instant('dialogs.createCampaignDialog.copyTitle');
    }

    return this.translate.instant(
      this.isEditMode() ? 'dialogs.createCampaignDialog.updateTitle' : 'dialogs.createCampaignDialog.createTitle',
    );
  });
  public readonly submitButtonText = computed(() => {
    if (this.isCopyMode()) {
      return this.translate.instant('dialogs.createCampaignDialog.copyCta');
    }

    return this.translate.instant(
      this.isEditMode() ? 'shared.form.save' : 'dialogs.createCampaignDialog.createCampaign',
    );
  });

  constructor() {
    super();

    this.fillFormWithCampaign();
    this.fetchSourceCampaignIfNeeded();

    effect(() => {
      const campaign = this.store.campaign$();
      const campaignSubmitLoading = this.store.campaignSubmitLoading$();

      if (campaign && !campaignSubmitLoading) {
        if (this.mode() !== 'edit') {
          this.eventTrackingService.track(EEventType.campaignCreationStarted, {
            campaignId: campaign.id,
            source: this.isCopyMode() ? 'copy' : this.briefModeSubmitted ? 'brief' : 'blank',
          });
        } else {
          this.trackCampaignNameIconUpdated(campaign);
        }

        this.router.navigate(['/campaigns', 'form', campaign.id], {
          ...(this.isCopyMode() ? { state: { openBasicsOnLoad: true } } : {}),
        });
        this.dialogRef.close({ iconId: campaign.iconId, name: campaign.name });
      }
    });

    effect(() => {
      const sourceCampaign = this.store.copySourceCampaign$();
      const sourceCampaignLoading = this.store.copySourceCampaignLoading$();

      if (!this.isCopyMode() || !this.copySubmitRequested()) {
        return;
      }

      if (sourceCampaignLoading) {
        return;
      }

      if (!sourceCampaign) {
        this.copySubmitRequested.set(false);

        return;
      }

      this.copySubmitRequested.set(false);
      this.submitCopyCampaign(sourceCampaign);
    });

    this.createBriefAnalysisEffects();
  }

  private createBriefAnalysisEffects(): void {
    effect(() => {
      const analysis = this.store.briefAnalysis$();

      if (!analysis) {
        return;
      }

      this.store.clearBriefAnalysis();
      this.handleBriefAnalysis(analysis);
    });

    merge(this.form.controls.briefFile.valueChanges, this.form.controls.briefFile.statusChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.syncNameRequiredWithBriefFile());
    this.syncNameRequiredWithBriefFile();

    effect(() => {
      if (this.store.briefAnalyzeLoading$()) {
        this.attachBriefAnalyzingOverlay();
      } else {
        this.detachBriefAnalyzingOverlay();
      }
    });

    // MatDialogRef's own disableClose (not app-side-dialog's - see the template's own
    // [disableClose] for that half) gates ESC/backdrop dismissal, which app-side-dialog's X
    // button doesn't go through. Without this, either path could silently discard an in-flight
    // 30-90s brief analysis with no confirmation, unlike the wizard dialog a step later which
    // explicitly guards the equivalent case.
    effect(() => {
      this.dialogRef.disableClose = this.store.briefAnalyzeLoading$();
    });
  }

  public ngOnDestroy(): void {
    this.detachBriefAnalyzingOverlay();
  }

  public onCancelBriefAnalyze(): void {
    this.store.cancelBriefAnalyze();
  }

  public onSubmit(): void {
    if (this.form.invalid || this.store.campaignSubmitLoading$()) {
      return;
    }

    const { iconId, name } = this.form.getRawValue();

    // Re-derived on every submit (not just set true on the brief branch) so a cancelled brief
    // analysis followed by a blank/copy submit in the same dialog session doesn't keep reporting
    // campaignCreationStarted with the stale source: 'brief' from the earlier attempt.
    this.briefModeSubmitted = this.hasValidBriefFile();

    if (this.briefModeSubmitted) {
      this.store.analyzeBrief(this.form.controls.briefFile.value!);

      return;
    }

    if (this.isCopyMode()) {
      this.copySubmitRequested.set(true);

      if (this.store.copySourceCampaignLoading$()) {
        return;
      }

      const sourceCampaign = this.store.copySourceCampaign$();

      if (!sourceCampaign) {
        this.copySubmitRequested.set(false);

        return;
      }

      this.copySubmitRequested.set(false);
      this.submitCopyCampaign(sourceCampaign);

      return;
    }

    if (this.data.campaign && this.data.campaignUpdatePayload) {
      this.store.updateCampaign({
        ...this.data.campaignUpdatePayload!,
        iconId: iconId!,
        id: this.data.campaign.id,
        name: name!,
        status: this.data.campaign.status ?? ECampaignStatus.draft,
      });
    } else {
      this.store.createCampaign({ iconId: iconId!, name: name!, status: ECampaignStatus.draft });
    }
  }

  public onImageSelected(event: { file: File; id: string }): void {
    const { iconId, imageFile } = this.form.controls;

    imageFile.setValue(event.file);
    iconId.setValue(event.id);
  }

  public onImageRemoved(): void {
    const { iconId, imageFile } = this.form.controls;

    imageFile.setValue(null);
    iconId.setValue(null);
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  public hasValidBriefFile(): boolean {
    const { briefFile } = this.form.controls;

    return !!briefFile.value && briefFile.valid;
  }

  private syncNameRequiredWithBriefFile(): void {
    const nameControl = this.form.controls.name;

    nameControl.setValidators(this.hasValidBriefFile() ? null : Validators.required);
    nameControl.updateValueAndValidity({ emitEvent: false });
  }

  // Branches on errors.length rather than analysis.canCreate - the backend's own explicit
  // "is this creatable" flag - since canCreate has no bearing on what's actually shown (the
  // wizard is driven entirely by errors[]/buildBriefStops). The two are expected to always agree
  // under the current backend contract; see docs/campaign-creation-from-brief.md for why
  // consulting canCreate here isn't a safe drop-in (an empty errors[] with canCreate: false would
  // leave the wizard with nothing to display and no way to let the user fix whatever's actually
  // wrong).
  private handleBriefAnalysis(analysis: IBriefAnalysis): void {
    if (analysis.errors.length) {
      this.openBriefWizard(analysis);

      return;
    }

    this.finalizeBriefCampaign(analysis.campaign, analysis.warnings);
  }

  private openBriefWizard(analysis: IBriefAnalysis): void {
    this.dialog
      .open(BriefWizardDialogComponent, { data: { analysis }, disableClose: true, width: '760px' })
      .afterClosed()
      .subscribe((result?: TBriefWizardDialogResult) => {
        if (result?.outcome === 'create') {
          this.finalizeBriefCampaign(result.campaign, []);
        }
      });
  }

  // Warnings surfaced directly from /brief/analyze (no errors, so the wizard is never opened) are
  // confirmed here; the wizard already runs the same confirmation itself before closing with
  // outcome: 'create', so this only ever sees an empty list in that path.
  private finalizeBriefCampaign(campaignDraft: Record<string, unknown>, warnings: IBriefValidationMessage[]): void {
    if (!warnings.length) {
      this.createCampaignFromBriefDraft(campaignDraft);

      return;
    }

    this.dialog
      .open(BriefWarningsDialogComponent, { data: { campaign: campaignDraft, warnings }, width: '480px' })
      .afterClosed()
      .subscribe((continueCreating: boolean) => {
        if (continueCreating) {
          this.createCampaignFromBriefDraft(campaignDraft);
        }
      });
  }

  private createCampaignFromBriefDraft(campaignDraft: Record<string, unknown>): void {
    const { iconId, name } = this.form.getRawValue();
    const apiPayload = toCampaignApiPayload(campaignDraft);
    const finalName = (apiPayload['name'] as string | null) || name;

    // name is normally required by the time errors.length is 0 (the backend routes a missing
    // name through the same basicsName stop as any other required basics field) - this is a
    // last-resort guard for a brief that somehow reaches here with neither its own name nor one
    // typed in this dialog (name is optional here while a brief file is selected), rather than
    // silently submitting a nameless campaign and relying on the backend to reject it.
    if (!finalName) {
      this.snackbarService.openError(this.translate.instant('dialogs.createCampaignDialog.briefMissingNameError'));

      return;
    }

    this.store.createCampaignFromBrief({
      ...apiPayload,
      iconId: iconId ?? null,
      name: finalName,
      status: ECampaignStatus.draft,
    });
  }

  private attachBriefAnalyzingOverlay(): void {
    if (this.briefAnalyzingOverlayRef) {
      return;
    }

    const template = this.briefAnalyzingOverlayTemplate();

    if (!template) {
      return;
    }

    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      height: '100%',
      maxHeight: '100vh',
      maxWidth: '100vw',
      positionStrategy: this.overlay.position().global().left('0').top('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100%',
    });

    overlayRef.attach(new TemplatePortal(template, this.viewContainerRef));
    this.briefAnalyzingOverlayRef = overlayRef;
  }

  private detachBriefAnalyzingOverlay(): void {
    this.briefAnalyzingOverlayRef?.dispose();
    this.briefAnalyzingOverlayRef = undefined;
  }

  private fillFormWithCampaign(): void {
    const campaignToFill = this.resolveCampaignToFill();

    if (!campaignToFill) {
      return;
    }

    const copyNameSuffix = this.translate.instant('dialogs.createCampaignDialog.copyNameSuffix');
    const iconId = campaignToFill.iconId ?? null;
    const name = this.isCopyMode() ? `${campaignToFill.name} ${copyNameSuffix}` : campaignToFill.name;

    this.form.patchValue({
      iconId,
      imageFile: iconId ? FileUploaderService.createCustomFile(iconId) : null,
      name,
    });
  }

  private fetchSourceCampaignIfNeeded(): void {
    const { adminMode = false } = this.data;
    const sourceCampaignId: string | null = this.getSourceCampaignId();

    if (!this.isCopyMode() || !sourceCampaignId) {
      return;
    }

    this.store.fetchSourceCampaignWithAll({
      adminMode,
      campaignId: sourceCampaignId,
    });
  }

  private submitCopyCampaign(sourceCampaign: TCampaignApi): void {
    const { iconId, name } = this.form.getRawValue();

    if (!name) {
      return;
    }

    this.store.copyCampaign({
      iconId: iconId ?? null,
      name,
      sourceCampaign,
    });
  }

  private trackCampaignNameIconUpdated(updatedCampaign: TCampaignApi): void {
    const originalCampaign = this.data.campaign;

    if (!originalCampaign) {
      return;
    }

    // CTR-1 (audit 2026-08-06) - campaign-wide totals on general events. The pre-edit campaign's
    // counts are correct here (a name/icon edit never changes creators/CTAs), and it's the only
    // reliable source in every open mode - the update response would also carry the joined arrays
    // (the backend serializes all children on update), but this dialog is only ever opened from
    // the form with the fully-loaded campaign, so the pre-edit source is sufficient.
    const counterProperties = {
      campaignCreationCreatorCounter: originalCampaign.campaignCreators?.length,
      campaignCreationDeliverableGroupCounter: originalCampaign.ctaTemplates?.length,
    };

    if (originalCampaign.name !== updatedCampaign.name) {
      this.eventTrackingService.track(EEventType.campaignCreationUpdated, {
        campaignId: updatedCampaign.id,
        newValue: updatedCampaign.name ?? undefined,
        previousValue: originalCampaign.name ?? undefined,
        source: 'name',
        ...counterProperties,
      });
    }

    if (originalCampaign.iconId !== updatedCampaign.iconId) {
      this.eventTrackingService.track(EEventType.campaignCreationUpdated, {
        campaignId: updatedCampaign.id,
        newValue: updatedCampaign.iconId ?? undefined,
        previousValue: originalCampaign.iconId ?? undefined,
        source: 'icon',
        ...counterProperties,
      });
    }
  }

  private resolveCampaignToFill(): Pick<TCampaignApi, 'iconId' | 'name'> | null {
    const { campaign, sourceCampaign } = this.data;

    if (this.isCopyMode()) {
      return sourceCampaign ?? null;
    }

    return campaign ?? null;
  }

  private getSourceCampaignId(): string | null {
    const { sourceCampaign } = this.data;

    if (!sourceCampaign || typeof sourceCampaign !== 'object') {
      return null;
    }

    return sourceCampaign.id ?? null;
  }
}
